import React from 'react'
import { Accordion } from '@/components/ui/accordion'
import {
  ObjectPanel,
  selectionHasLocked,
  shapesByIdFromManager,
} from '@/components/app/panels/left-panel/object-panel/object-panel'
import { TypePanel } from '@/components/app/panels/left-panel/type-panel/type-panel'
import { PhotosPanel } from '@/components/app/panels/left-panel/photos-panel/photos-panel'
import { ClikArtPanel } from '@/components/app/panels/left-panel/clikart-panel'
import { LayoutPanel } from '@/components/app/panels/left-panel/layout-panel/layout-panel'
import { EffectsPanel } from '@/components/app/panels/left-panel/effects-panel/effects-panel'
import { useActiveSelection } from '@/components/app/editor/active-object-context'
import { useDocumentLayout } from '@/contexts/document-layout-context'
import { useEditorCommandState } from '@/contexts/editor-command-state-context'
import { useEditorCommands } from '@/contexts/editor-commands-context'
import { useEditorSettings } from '@/contexts/editor-settings-context'
import { useEditorTools } from '@/contexts/editor-tools-context'
import { useTextDefaults } from '@/contexts/text-defaults-context'
import { computePropertiesPanelApplicability } from '@/lib/panels/properties-panel-applicability'
import { PropertiesSection } from './components/properties-section'
import { resolveSelectedClikArtShape } from './helpers/selected-clikart-shape'
import { resolveSelectedPhotoShape } from './helpers/selected-photo-shape'
import type { SelectToolProps } from './types/select-tool-props'

export const SelectTool: React.FC<SelectToolProps> = ({
  openSectionIds,
  onOpenSectionIdsChange,
  registerSectionElement,
  shapeManagerRef,
  isCornerResizeActive,
  onShapeCommitted,
  onBatchShapesCommitted,
  textPanelRevision = 0,
  onLayerReorder,
  onUnifiedLayerArrange,
  selectedShapeTextParagraphs,
  isSelectedShapeTextFrame,
  unifiedLayerIds = [],
  textChains,
  onTextFramesLinked,
}) => {
  const {
    bleedWidth,
    bleedHeight,
    spreadIndex,
    rightPageOriginX,
    rightPageOriginY,
    mirrorSpineAxisX,
    spreadId,
  } = useDocumentLayout()
  const { showGuides } = useEditorSettings()
  const { defaultFontFamilyId, defaultFontName, defaultFontSize } = useTextDefaults()
  const { isNodeEditMode, setMirrorMoveHovering: onMirrorMoveHoverChange } = useEditorTools()
  const commands = useEditorCommands()
  const commandState = useEditorCommandState()
  const {
    isInGroupIsolationMode,
    textStyleDisabled,
    lockedSelectionDisablesPropertyPanels,
    photoEffectsOpen,
    isRemovingBackgroundForSelection: isRemovingBackground,
  } = commandState
  // Same availability rules home applied when it passed these as props: a command that isn't
  // available is `undefined`, which is how the panels hide or disable it.
  const onEditPath = commandState.canEditSelectedPath ? commands.editSelectedPath : undefined
  const onTextOnPath = commandState.textOnPathDisabled ? undefined : commands.textOnPath
  const onAddCaption = commandState.captionEnabled ? commands.addCaption : undefined
  const isControlled = openSectionIds != null && onOpenSectionIdsChange != null
  // Without live position: nothing here shows where the selection sits, so a move drag skips it.
  const { activeObject, activeObjects, activeTextBoxes } = useActiveSelection()

  // Ancestor-aware (a shape under a locked group is effectively locked too) — reuses the same
  // check `ObjectPanel` uses internally, ORed with the live-selection-snapshot flag from home.tsx
  // (`lockedSelectionDisablesPropertyPanels`) so neither source's blind spot regresses the other.
  const shapesById = shapesByIdFromManager(shapeManagerRef?.current)
  const hasLockedSelection =
    lockedSelectionDisablesPropertyPanels ||
    selectionHasLocked(activeObjects, shapesById) ||
    selectionHasLocked(activeTextBoxes, shapesById)

  // Recomputed on every render straight from the live selection — never cached across a selection
  // change, so applicability can't go stale (adding/removing an object immediately re-derives it).
  // `allShapes` lets a single-group selection's Type/Photo reason reflect what's actually inside it
  // (still never editable — see `computePropertiesPanelApplicability`'s doc comment).
  const applicability = React.useMemo(
    () =>
      computePropertiesPanelApplicability({
        shapes: activeObjects,
        textBoxes: activeTextBoxes,
        hasLockedSelection,
        allShapes: shapeManagerRef?.current?.getShapes() ?? [],
      }),
    [activeObjects, activeTextBoxes, hasLockedSelection, shapeManagerRef]
  )

  // The placed photo surfaced in the Photo section (see `resolveSelectedPhotoShape`).
  const selectedPhotoShape = React.useMemo(
    () =>
      resolveSelectedPhotoShape(
        activeObject,
        applicability.photo.applicable,
        applicability.photo.readOnlyPhoto
      ),
    [activeObject, applicability.photo.applicable, applicability.photo.readOnlyPhoto]
  )

  // A placed ClikArt takes the same slot instead: relabelled, with its own restricted actions.
  const selectedClikArtShape = React.useMemo(
    () =>
      resolveSelectedClikArtShape(
        activeObject,
        applicability.photo.applicable,
        applicability.photo.readOnlyPhoto
      ),
    [activeObject, applicability.photo.applicable, applicability.photo.readOnlyPhoto]
  )

  return (
    <Accordion
      type="multiple"
      {...(isControlled ? { value: openSectionIds, onValueChange: onOpenSectionIdsChange } : {})}
      className="flex w-full flex-col gap-4"
    >
      <PropertiesSection
        value="object"
        label="Object"
        reason={applicability.object.reason}
        registerSectionElement={registerSectionElement}
      >
        <ObjectPanel
          disabled={!applicability.object.applicable}
          shapeManagerRef={shapeManagerRef}
          isCornerResizeActive={isCornerResizeActive}
          bleedWidth={bleedWidth}
          bleedHeight={bleedHeight}
          spreadIndex={spreadIndex}
          rightPageOriginX={rightPageOriginX}
          rightPageOriginY={rightPageOriginY}
          onShapeCommitted={onShapeCommitted}
          onBatchShapesCommitted={onBatchShapesCommitted}
          showGuides={showGuides}
          isInGroupIsolationMode={isInGroupIsolationMode}
          onEditPath={onEditPath}
          onTextOnPath={onTextOnPath}
        />
      </PropertiesSection>

      <PropertiesSection
        value="type"
        label="Type"
        reason={applicability.type.reason}
        registerSectionElement={registerSectionElement}
      >
        <TypePanel
          selectedShapeTextParagraphs={selectedShapeTextParagraphs}
          isSelectedShapeTextFrame={isSelectedShapeTextFrame}
          restoreShapeTextFocus={commands.restoreShapeTextFocus}
          getEditingShapeTextSelectionStyle={commands.getEditingShapeTextSelectionStyle}
          getEditingShapeTextId={commands.getEditingShapeTextId}
          getEditingShapeTextKerningState={commands.getEditingShapeTextKerningState}
          shapeTextEditRevision={commandState.shapeTextEditRevision}
          disabled={!applicability.type.applicable || textStyleDisabled || isNodeEditMode}
          onStyleUpdate={commands.applyTextStyle}
          applyKerningAtCaret={commands.applyKerningAtCaret}
          spreadId={spreadId}
          textPanelRevision={textPanelRevision}
          defaultFontFamilyId={defaultFontFamilyId}
          defaultFontName={defaultFontName}
          defaultFontSize={defaultFontSize}
        />
      </PropertiesSection>

      <PropertiesSection
        value="photo"
        label={selectedClikArtShape ? 'ClikArt' : 'Photo'}
        reason={applicability.photo.reason}
        registerSectionElement={registerSectionElement}
      >
        {selectedClikArtShape ? (
          <ClikArtPanel
            selectedClikArtShape={selectedClikArtShape}
            interactionLocked={!applicability.photo.applicable}
            onCropClikArt={commands.cropPhoto}
            onClippingPath={onEditPath}
            onRemoveClikArt={commands.removePhotoFromPanel}
          />
        ) : (
          <PhotosPanel
            selectedPhotoShape={selectedPhotoShape}
            interactionLocked={!applicability.photo.applicable}
            onAddCaption={onAddCaption}
            onCropPhoto={commands.cropPhoto}
            onRemovePhoto={commands.removePhotoFromPanel}
            onRemoveBackground={commands.removeBackgroundFromPanel}
            onPhotoEffects={commands.togglePhotoEffects}
            photoEffectsOpen={photoEffectsOpen}
            onClippingPath={selectedPhotoShape && onEditPath ? onEditPath : undefined}
            isRemovingBackground={isRemovingBackground}
          />
        )}
      </PropertiesSection>

      <PropertiesSection
        value="layout"
        label="Layout"
        reason={applicability.layout.reason}
        registerSectionElement={registerSectionElement}
      >
        <LayoutPanel
          shapeManagerRef={shapeManagerRef}
          onBatchShapesCommitted={onBatchShapesCommitted}
          onLayerReorder={onLayerReorder}
          onUnifiedLayerArrange={onUnifiedLayerArrange}
          interactionLocked={!applicability.layout.applicable}
          unifiedLayerIds={unifiedLayerIds}
          mirrorSpineAxisX={mirrorSpineAxisX}
          onMirrorMoveHoverChange={onMirrorMoveHoverChange}
          textChains={textChains}
          onTextFramesLinked={onTextFramesLinked}
        />
      </PropertiesSection>

      <PropertiesSection
        value="effects"
        label="Effects"
        reason={applicability.effects.reason}
        registerSectionElement={registerSectionElement}
      >
        <EffectsPanel
          shapeManagerRef={shapeManagerRef}
          onShapeCommitted={onShapeCommitted}
          onBatchShapesCommitted={onBatchShapesCommitted}
          disabled={!applicability.effects.applicable}
          onTransparencyCommitApplied={commands.transparencyCommitApplied}
        />
      </PropertiesSection>
    </Accordion>
  )
}
